// Nominal Labour Times section of the job Details tab. One hours
// field per live stage (the 8 welding bays collapse into a single
// "Welding" field — see lib/nominalHours). These are job-level
// totals, not per-spool: the Spools tab's "Distribute nominal hrs"
// button splits each figure evenly across the job's spools into the
// existing per-spool stage hours.
import { useMemo } from "react";
import { useActiveStages } from "../components/StagesProvider";
import {
  nominalRows,
  nominalTotal,
  setNominalHours,
} from "../lib/nominalHours";

interface Props {
  value: Record<string, number>;
  onChange(next: Record<string, number>): void;
}

export function NominalHoursFields({ value, onChange }: Props) {
  const catalogue = useActiveStages();
  const rows = useMemo(() => nominalRows(catalogue), [catalogue]);
  const total = nominalTotal(value);
  const budgeted = rows.filter((r) => (value[r.key] ?? 0) > 0).length;

  return (
    <>
      <div className="modal-section" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span>Nominal Labour Times</span>
        <span style={{
          marginLeft: "auto",
          fontFamily: "var(--bsp-font-mono)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0,
          textTransform: "none",
          color: total > 0 ? "var(--bsp-dark)" : "var(--bsp-fg-muted)",
        }}>
          {total.toFixed(1)}h total · {budgeted} stage{budgeted === 1 ? "" : "s"}
        </span>
      </div>
      <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginBottom: 10, lineHeight: 1.45 }}>
        Whole-job budget per stage. Use <strong>⏱ Distribute nominal hrs</strong> on the
        Spools tab to split each figure evenly across this job&rsquo;s spools into their stage
        hours. Blank stages are left untouched, and stages a spool has already completed keep
        their recorded hours.
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: 10,
        marginBottom: 12,
      }}>
        {rows.map((r) => (
          <div className="form-group" key={r.key}>
            <label
              htmlFor={`nominal-${r.key}`}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
              title={`${r.label} — nominal hours for the whole job`}
            >
              <span style={{
                background: r.color,
                color: "#fff",
                borderRadius: 3,
                padding: "1px 4px",
                fontSize: 9,
                fontFamily: "var(--bsp-font-mono)",
                letterSpacing: "0.02em",
              }}>{r.short}</span>
              <span style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>{r.label}</span>
            </label>
            <input
              id={`nominal-${r.key}`}
              type="number"
              min={0}
              step="0.5"
              inputMode="decimal"
              placeholder="0"
              value={value[r.key] ?? ""}
              onChange={(e) => onChange(setNominalHours(value, r.key, e.target.value))}
              style={{ fontFamily: "var(--bsp-font-mono)", fontVariantNumeric: "tabular-nums" }}
            />
          </div>
        ))}
      </div>
    </>
  );
}
